import { Injectable } from '@angular/core'
import { reactor } from '../reactor'
import { customerOperations } from '../stores/customer-store'
import { KeywordsService } from '../services/keywords-service'
import { DocumentsService } from '../services/documents-service'
import { UploadsService } from '../services/uploads-service'
import { WritersService } from '../services/writers-service'
import { errorToast, successToast } from '../toaster'
import { donePromise } from '../utils'
import uniq from 'lodash-es/uniq'
import random from 'lodash-es/random'
import { Keyword } from '../types'

@Injectable({ providedIn: 'root' })
export class KeywordActions {
  keywordsService = new KeywordsService()
  documentsService = new DocumentsService()
  uploadsService = new UploadsService()
  writersService = new WritersService()

  loadKeywords(customerId: number, state: string = null): Promise<void> {
    let p = state
      ? this.keywordsService.getKeywordsByState(customerId, state)
      : this.keywordsService.getKeywords(customerId)
    const req = p
      .then((keywords) => {
        reactor.dispatch(customerOperations.setKeywords, { customerId, keywords })
      })
      .catch(() => {
        errorToast('An error occurred while loading topics, please reload this page')
      })
    return donePromise(req)
  }

  loadKeywordsWithWriters(customerId: number, scope: string): void {
    this.keywordsService.getKeywordsByScope(customerId, scope).then((keywords) => {
      const writerIds = uniq(
        keywords
          .filter((k) => k.writerId !== null)
          .map((k) => k.writerId)
          .concat(
            keywords.filter((k) => k.previousWriterId !== null).map((k) => k.previousWriterId)
          )
      )
      this.writersService.getWriters(writerIds).then((writers) => {
        reactor.batch(() => {
          reactor.dispatch(customerOperations.setKeywords, { customerId, keywords })
          reactor.dispatch(customerOperations.setWriters, writers)
        })
      })
    })
  }

  updateKeyword(customerId: number, keywordId: number, updates: Object): Promise<void> {
    const req = this.keywordsService
      .updateKeyword(customerId, keywordId, updates)
      .then(() => {
        updates['editing'] = false
        updates['expanded'] = false
        reactor.dispatch(customerOperations.updateKeyword, { customerId, keywordId, updates })
        successToast('Request updated')
      })
      .catch((res) => {
        updates['editing'] = true
        updates['expanded'] = true
        reactor.dispatch(customerOperations.updateKeyword, { customerId, keywordId, updates })
        const errors = <string[]>res.body
        errorToast('An error occurred while updating your topic: <br><br>' + errors && errors[0])
      })
    return donePromise(req)
  }

  createKeyword(customerId: number, keyword: object, files: File[] = []): Promise<void> {
    return this.keywordsService
      .createKeyword(customerId, keyword)
      .then((keyword) => {
        reactor.dispatch(customerOperations.createKeyword, { keyword })
        successToast('Request created')

        if (files.length > 0) {
          const filePromises = files.map((f) => this.uploadFile(f))
          Promise.all(filePromises).then((urls) => {
            const createDocPromises = urls.map((url, i) => {
              return this.documentsService.createDocument(customerId, keyword.id, {
                fileUrl: url,
                name: files[i].name,
              })
            })
            Promise.all(createDocPromises).then((docs) => {
              reactor.batch(() => {
                docs.forEach((d) => {
                  reactor.dispatch(customerOperations.addDocument, {
                    customerId,
                    keywordId: keyword.id,
                    document: d,
                  })
                })
              })
            })
          })
        }
      })
      .catch(() => {
        errorToast('An error occurred while creating your topic, please try again')
      })
  }

  deleteKeyword(customerId: number, keywordId: number) {
    const req = this.keywordsService
      .deleteKeyword(customerId, keywordId)
      .then(() => {
        reactor.dispatch(customerOperations.deleteKeyword, { customerId, keywordId })
        successToast('Request deleted')
      })
      .catch(() => {
        errorToast('An error occurred while deleting your request, please try again')
      })
    return donePromise(req)
  }

  uploadFile(f: File): Promise<any> {
    const reader = new FileReader()
    const p = new Promise((resolve, reject) => {
      reader.onloadend = () => {
        this.uploadsService
          .uploadFile(f.name, f.type, reader.result)
          .then((resp) => {
            resolve(resp.url)
          })
          .catch((e) => {
            errorToast('Document ' + f.name + ' could not be uploaded, please try again')
            reject()
          })
      }
    })
    reader.readAsArrayBuffer(f)
    return p
  }

  toggleKeywordExpanded(keyword: Keyword, expanded: boolean) {
    reactor.dispatch(customerOperations.updateKeyword, {
      customerId: keyword.customerId,
      keywordId: keyword.id,
      updates: { expanded },
    })
  }

  toggleKeywordEditing(keyword: Keyword, editing: boolean) {
    reactor.dispatch(customerOperations.updateKeyword, {
      customerId: keyword.customerId,
      keywordId: keyword.id,
      updates: { editing, expanded: editing },
    })
  }

  addDocument(keyword: Keyword, file: File) {
    const tempId = 'temp-' + random(0, 9999)
    const customerId = keyword.customerId
    const keywordId = keyword.id
    reactor.batch(() => {
      reactor.dispatch(customerOperations.updateKeyword, {
        customerId,
        keywordId,
        updates: keyword,
      })
      reactor.dispatch(customerOperations.addDocument, {
        customerId,
        keywordId,
        document: { name: file.name, id: tempId, pending: true },
      })
    })
    this.uploadFile(file)
      .then((url) => {
        this.documentsService
          .createDocument(customerId, keywordId, { fileUrl: url, name: file.name })
          .then((doc) => {
            reactor.batch(() => {
              reactor.dispatch(customerOperations.removeDocument, {
                customerId,
                keywordId,
                id: tempId,
              })
              reactor.dispatch(customerOperations.addDocument, {
                customerId,
                keywordId,
                document: doc,
              })
            })
          })
      })
      .catch(() => {
        reactor.dispatch(customerOperations.removeDocument, { customerId, keywordId, id: tempId })
      })
  }

  removeDocument(keyword: Keyword, id: number) {
    const customerId = keyword.customerId
    const keywordId = keyword.id
    this.documentsService.removeDocument(customerId, keywordId, id).then(() => {
      reactor.batch(() => {
        reactor.dispatch(customerOperations.updateKeyword, {
          customerId,
          keywordId,
          updates: keyword,
        })
        reactor.dispatch(customerOperations.removeDocument, { customerId, keywordId, id })
      })
    })
  }

  acceptKeyword(customerId: number, keywordId: number): Promise<any> {
    const req = this.keywordsService
      .acceptKeyword(customerId, keywordId)
      .then((keyword) => {
        const updates = { acceptedAt: keyword.acceptedAt, state: keyword.state }
        reactor.dispatch(customerOperations.updateKeyword, { customerId, keywordId, updates })
        successToast(
          `You’ve accepted a topic! You may find it on the Requests tab when you’re ready to activate it.`
        )
      })
      .catch(() => {
        errorToast('Error accepting topic, please try again later.')
      })
    return donePromise(req)
  }

  declineKeyword(customerId: number, keywordId: number): Promise<any> {
    const req = this.keywordsService
      .declineKeyword(customerId, keywordId)
      .then(() => {
        reactor.dispatch(customerOperations.updateKeyword, {
          customerId,
          keywordId,
          updates: { state: 'declined' },
        })
        successToast('Topic declined.')
      })
      .catch(() => {
        errorToast('Error declining topic, please try again later.')
      })
    return donePromise(req)
  }
}
